import React from "react";
import styled, { keyframes } from "styled-components";
import { colors, getMediumRGBA } from "../../styles/colors";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageWrapper = styled.div`
  font-family: "Georgia", "Times New Roman", serif;
  line-height: 1.7;
  color: ${colors.dark};
  min-height: 100vh;
  width: 100%;
`;

// Top Bar
const TopBar = styled.div`
  background: ${colors.dark};
  color: white;
  padding: 8px 20px;
  font-size: 13px;
  font-family: "Helvetica Neue", Arial, sans-serif;
`;

const TopBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    justify-content: center;
    text-align: center;
  }
`;

const TopBarItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.9;

  a {
    color: white;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Header = styled.header`
  background: ${colors.white};
  padding: 20px 0;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LogoIcon = styled.div`
  width: 55px;
  height: 55px;
  background: ${colors.gradientIntermediate};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  box-shadow: 0 4px 15px ${getMediumRGBA(0.3)};
`;

const LogoText = styled.div`
  h1 {
    font-size: 22px;
    font-weight: 600;
    color: ${colors.dark};
    margin: 0;
    letter-spacing: -0.5px;
  }

  span {
    font-size: 12px;
    color: ${colors.medium};
    text-transform: uppercase;
    letter-spacing: 2px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 8px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const NavLink = styled.a<{ $active?: boolean }>`
  color: ${(props) => (props.$active ? colors.primary : colors.dark)};
  text-decoration: none;
  font-weight: 500;
  font-size: 15px;
  padding: 10px 18px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: "Helvetica Neue", Arial, sans-serif;
  background: ${(props) =>
    props.$active ? getMediumRGBA(0.08) : "transparent"};

  &:hover {
    color: ${colors.primary};
    background: ${getMediumRGBA(0.08)};
  }
`;

const Hero = styled.section`
  background:
    linear-gradient(165deg, ${getMediumRGBA(0.92)}, rgba(30, 53, 66, 0.95)),
    url("https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1600&q=80")
      center/cover fixed;
  color: white;
  padding: 120px 20px 140px;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px 100px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeInUp} 1s ease-out;
`;

const HeroBadge = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 13px;
  font-family: "Helvetica Neue", Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  margin-bottom: 25px;
  font-weight: 400;
  line-height: 1.2;
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 22px;
  max-width: 600px;
  margin: 0 auto 40px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled.button<{ $variant?: "primary" | "secondary" }>`
  background: ${(props) =>
    props.$variant === "secondary" ? "transparent" : colors.primary};
  color: ${(props) =>
    props.$variant === "secondary" ? "white" : colors.white};
  padding: 16px 36px;
  border: ${(props) =>
    props.$variant === "secondary" ? "2px solid white" : "none"};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Helvetica Neue", Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    background: ${(props) =>
      props.$variant === "secondary" ? "white" : colors.dark};
    color: ${(props) =>
      props.$variant === "secondary" ? colors.dark : colors.white};
  }
`;

const TrustBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 50px;
  flex-wrap: wrap;
  opacity: 0.9;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-family: "Helvetica Neue", Arial, sans-serif;
`;

// About Section
const AboutSection = styled.section`
  padding: 100px 20px;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const AboutImage = styled.div`
  position: relative;

  img {
    width: 100%;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: "";
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100px;
    height: 100px;
    background: ${colors.primary};
    opacity: 0.1;
    border-radius: 16px;
  }
`;

const AboutText = styled.div`
  h2 {
    font-size: 38px;
    color: ${colors.dark};
    margin-bottom: 20px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 30px;
    }
  }

  p {
    color: ${colors.medium};
    font-size: 17px;
    margin-bottom: 20px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
  font-family: "Helvetica Neue", Arial, sans-serif;
  color: ${colors.dark};

  &::before {
    content: "✓";
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: ${colors.success};
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;

  span {
    display: inline-block;
    background: ${getMediumRGBA(0.1)};
    color: ${colors.primary};
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 12px;
    font-family: "Helvetica Neue", Arial, sans-serif;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 15px;
  }

  h2 {
    font-size: 40px;
    color: ${colors.dark};
    margin-bottom: 15px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 30px;
    }
  }

  p {
    color: ${colors.medium};
    font-size: 18px;
    max-width: 600px;
    margin: 0 auto;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 35px 30px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${colors.gradientBeginner};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${getMediumRGBA(0.1)};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h3`
  color: ${colors.dark};
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
  font-family: "Helvetica Neue", Arial, sans-serif;
`;

const ServiceDescription = styled.p`
  color: ${colors.medium};
  font-size: 15px;
  line-height: 1.6;
  font-family: "Helvetica Neue", Arial, sans-serif;
`;

// Team Section
const TeamSection = styled.section`
  padding: 100px 20px;
  background: ${colors.light};

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const TeamGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const TeamImage = styled.div`
  height: 280px;
  background: ${colors.gradientIntermediate};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 80px;
`;

const TeamInfo = styled.div`
  padding: 25px;
  text-align: center;

  h3 {
    color: ${colors.dark};
    font-size: 20px;
    margin-bottom: 5px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }

  span {
    color: ${colors.primary};
    font-size: 14px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }

  p {
    color: ${colors.medium};
    font-size: 14px;
    margin-top: 15px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
`;

// Contact Section
const ContactSection = styled.section`
  padding: 100px 20px;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const ContactWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  margin-top: 60px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: ${colors.background};
  padding: 30px;
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.light};
  }
`;

const ContactCardIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${colors.gradientBeginner};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 20px;
`;

const ContactCardTitle = styled.h3`
  color: ${colors.dark};
  font-size: 18px;
  margin-bottom: 10px;
  font-family: "Helvetica Neue", Arial, sans-serif;
`;

const ContactCardText = styled.p`
  color: ${colors.medium};
  font-size: 15px;
  line-height: 1.6;
  font-family: "Helvetica Neue", Arial, sans-serif;

  a {
    color: ${colors.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MapPlaceholder = styled.div`
  background: ${colors.gradientIntermediate};
  border-radius: 16px;
  height: 100%;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  box-shadow: 0 10px 40px ${getMediumRGBA(0.3)};
`;

// CTA Section
const CTASection = styled.section`
  padding: 80px 20px;
  background: ${colors.gradientAdvanced};
  text-align: center;
  color: white;
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;

  h2 {
    font-size: 36px;
    margin-bottom: 20px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  p {
    font-size: 18px;
    opacity: 0.9;
    margin-bottom: 30px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
`;

const CTAButton = styled.button`
  background: ${colors.primary};
  color: ${colors.white};
  padding: 18px 45px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Helvetica Neue", Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    background: ${colors.white};
    color: ${colors.dark};
  }
`;

const Footer = styled.footer`
  background: ${colors.dark};
  color: white;
  padding: 60px 20px 30px;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  h4 {
    color: ${colors.primary};
    font-size: 16px;
    margin-bottom: 20px;
    font-family: "Helvetica Neue", Arial, sans-serif;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  p,
  a {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    line-height: 2;
    font-family: "Helvetica Neue", Arial, sans-serif;
    text-decoration: none;
    display: block;
  }

  a:hover {
    color: white;
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 40px auto 0;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }

  @media (max-width: 600px) {
    justify-content: center;
    text-align: center;
  }
`;

// Services data
const services = [
  {
    icon: "🦴",
    title: "Krankengymnastik",
    description:
      "Individuelle Übungsprogramme zur Wiederherstellung und Verbesserung der Beweglichkeit und Kraft nach Verletzungen oder Operationen.",
  },
  {
    icon: "🤲",
    title: "Manuelle Therapie",
    description:
      "Spezielle Handgriff-Techniken zur Behandlung von Gelenkfunktionsstörungen und zur Linderung von akuten und chronischen Schmerzen.",
  },
  {
    icon: "⚡",
    title: "Elektrotherapie",
    description:
      "Moderne Strombehandlungen zur Schmerzlinderung, Durchblutungsförderung und Muskelstimulation.",
  },
  {
    icon: "🌡️",
    title: "Wärme- & Kältetherapie",
    description:
      "Fango, Heißluft, Rotlicht und Kryotherapie zur Muskelentspannung und Entzündungshemmung.",
  },
  {
    icon: "💧",
    title: "Lymphdrainage",
    description:
      "Sanfte Massage-Technik zur Förderung des Lymphabflusses bei Schwellungen, Ödemen und nach Operationen.",
  },
  {
    icon: "🏃",
    title: "Sportphysiotherapie",
    description:
      "Spezialisierte Behandlungen für Sportler zur schnellen Rehabilitation und Prävention von Verletzungen.",
  },
];

// Team data
const team = [
  {
    name: "Dr. Maria Mustermann",
    role: "Praxisinhaberin",
    description:
      "Spezialistin für Orthopädie und Sportmedizin mit 15 Jahren Erfahrung",
    avatar: "👩‍⚕️",
  },
  {
    name: "Thomas Schmidt",
    role: "Physiotherapeut",
    description: "Experte für manuelle Therapie und Krankengymnastik",
    avatar: "👨‍⚕️",
  },
  {
    name: "Sarah Weber",
    role: "Physiotherapeutin",
    description: "Spezialistin für Lymphdrainage und Sportphysiotherapie",
    avatar: "👩‍⚕️",
  },
];

export const WidgetTest = () => {
  const chainId = "recyMqIQYYXJOZ3d0";
  // Inject widget script on mount
  React.useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://patient.physiofit.app/widget/physiofit-loader.js";
    script.setAttribute("data-chain-id", chainId);
    script.setAttribute(
      "data-logo-url",
      "https://cdn.prod.website-files.com/683029d80b4a6387eee2de07/683029d80b4a6387eee2df26_Physiofit_Logo_BlueDark.svg",
    );
    script.setAttribute("data-locale", "de");
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleHeroClick = () => {
    // open new tab
    window.open(
      `https://patient.physiofit.app/appointment/type?chainId=${chainId}`,
      "_blank",
    );
  };

  return (
    <PageWrapper>
      {/* Top Bar */}
      <TopBar>
        <TopBarContent>
          <TopBarItem>
            📞 <a href="tel:+4912345678900">+49 (0) 1234 567890</a>
          </TopBarItem>
          <TopBarItem>📍 Gesundheitsweg 12, 80331 München</TopBarItem>
          <TopBarItem>🕐 Mo-Fr: 8:00 - 18:00 Uhr</TopBarItem>
        </TopBarContent>
      </TopBar>

      {/* Header */}
      <Header>
        <HeaderContent>
          <LogoContainer>
            <LogoIcon>🌿</LogoIcon>
            <LogoText>
              <h1>Praxis Mustermann</h1>
              <span>Physiotherapie & Rehabilitation</span>
            </LogoText>
          </LogoContainer>
          <Nav>
            <NavLink $active>Startseite</NavLink>
            <NavLink>Leistungen</NavLink>
            <NavLink>Über uns</NavLink>
            <NavLink>Team</NavLink>
            <NavLink>Kontakt</NavLink>
          </Nav>
        </HeaderContent>
      </Header>

      {/* Hero Section */}
      <Hero>
        <HeroContent>
          <HeroBadge>Willkommen in unserer Praxis</HeroBadge>
          <HeroTitle>Ihre Gesundheit liegt uns am Herzen</HeroTitle>
          <HeroSubtitle>
            Moderne Physiotherapie mit ganzheitlichem Ansatz. Individuell
            abgestimmte Behandlungen für Ihr persönliches Wohlbefinden.
          </HeroSubtitle>
          <HeroButtons>
            <HeroButton onClick={handleHeroClick}>
              Termin vereinbaren
            </HeroButton>
          </HeroButtons>
          <TrustBadges>
            <TrustBadge>✓ Alle Kassen</TrustBadge>
            <TrustBadge>✓ Privatpatienten</TrustBadge>
            <TrustBadge>✓ Selbstzahler</TrustBadge>
            <TrustBadge>✓ Hausbesuche</TrustBadge>
          </TrustBadges>
        </HeroContent>
      </Hero>

      {/* About Section */}
      <AboutSection>
        <AboutContent>
          <AboutImage>
            <img
              src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80"
              alt="Physiotherapie Behandlung"
            />
          </AboutImage>
          <AboutText>
            <h2>Über unsere Praxis</h2>
            <p>
              Seit über 20 Jahren sind wir Ihr kompetenter Partner für
              Physiotherapie in München. Mit einem Team aus erfahrenen
              Therapeuten bieten wir Ihnen eine umfassende Betreuung auf
              höchstem Niveau.
            </p>
            <p>
              Unsere moderne Praxis vereint bewährte Behandlungsmethoden mit
              neuesten wissenschaftlichen Erkenntnissen. Dabei steht immer der
              Mensch im Mittelpunkt unserer Arbeit.
            </p>
            <FeatureList>
              <FeatureItem>Modernste Behandlungsmethoden</FeatureItem>
              <FeatureItem>Erfahrenes Therapeuten-Team</FeatureItem>
              <FeatureItem>Individuelle Behandlungspläne</FeatureItem>
              <FeatureItem>Barrierefreie Räumlichkeiten</FeatureItem>
            </FeatureList>
          </AboutText>
        </AboutContent>
      </AboutSection>

      {/* Services Section */}
      <Section>
        <SectionTitle>
          <span>Unsere Leistungen</span>
          <h2>Professionelle Physiotherapie</h2>
          <p>
            Entdecken Sie unser umfangreiches Behandlungsangebot für Ihre
            Gesundheit
          </p>
        </SectionTitle>
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Section>

      {/* Team Section */}
      <TeamSection>
        <SectionTitle>
          <span>Unser Team</span>
          <h2>Kompetenz & Erfahrung</h2>
          <p>
            Lernen Sie unser engagiertes Team aus qualifizierten Therapeuten
            kennen
          </p>
        </SectionTitle>
        <TeamGrid>
          {team.map((member, index) => (
            <TeamCard key={index}>
              <TeamImage>{member.avatar}</TeamImage>
              <TeamInfo>
                <h3>{member.name}</h3>
                <span>{member.role}</span>
                <p>{member.description}</p>
              </TeamInfo>
            </TeamCard>
          ))}
        </TeamGrid>
      </TeamSection>

      {/* Contact Section */}
      <ContactSection>
        <ContactWrapper>
          <SectionTitle>
            <span>Kontakt</span>
            <h2>So erreichen Sie uns</h2>
            <p>
              Wir freuen uns auf Ihre Anfrage und beraten Sie gerne persönlich
            </p>
          </SectionTitle>
          <ContactGrid>
            <ContactInfo>
              <ContactCard>
                <ContactCardIcon>📍</ContactCardIcon>
                <ContactCardTitle>Adresse</ContactCardTitle>
                <ContactCardText>
                  Gesundheitsweg 12
                  <br />
                  80331 München
                </ContactCardText>
              </ContactCard>
              <ContactCard>
                <ContactCardIcon>📞</ContactCardIcon>
                <ContactCardTitle>Telefon</ContactCardTitle>
                <ContactCardText>
                  <a href="tel:+4912345678900">+49 (0) 1234 567890</a>
                  <br />
                  Fax: +49 (0) 1234 567891
                </ContactCardText>
              </ContactCard>
              <ContactCard>
                <ContactCardIcon>🕐</ContactCardIcon>
                <ContactCardTitle>Öffnungszeiten</ContactCardTitle>
                <ContactCardText>
                  Mo - Fr: 8:00 - 18:00 Uhr
                  <br />
                  Sa: nach Vereinbarung
                </ContactCardText>
              </ContactCard>
              <ContactCard>
                <ContactCardIcon>✉️</ContactCardIcon>
                <ContactCardTitle>E-Mail</ContactCardTitle>
                <ContactCardText>
                  <a href="mailto:info@praxis-mustermann.de">
                    info@praxis-mustermann.de
                  </a>
                </ContactCardText>
              </ContactCard>
            </ContactInfo>
            <MapPlaceholder>🗺️</MapPlaceholder>
          </ContactGrid>
        </ContactWrapper>
      </ContactSection>

      {/* CTA Section */}
      <CTASection>
        <CTAContent>
          <h2>Bereit für Ihre erste Behandlung?</h2>
          <p>
            Vereinbaren Sie jetzt Ihren Termin und machen Sie den ersten Schritt
            zu mehr Lebensqualität. Wir beraten Sie gerne!
          </p>
          <CTAButton onClick={handleHeroClick}>Jetzt Termin anfragen</CTAButton>
        </CTAContent>
      </CTASection>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterColumn>
            <h4>Praxis Mustermann</h4>
            <p>
              Ihre Gesundheit ist unser Anliegen. Mit über 20 Jahren Erfahrung
              bieten wir Ihnen professionelle Physiotherapie auf höchstem
              Niveau.
            </p>
          </FooterColumn>
          <FooterColumn>
            <h4>Leistungen</h4>
            <a href="#">Krankengymnastik</a>
            <a href="#">Manuelle Therapie</a>
            <a href="#">Lymphdrainage</a>
            <a href="#">Sportphysiotherapie</a>
          </FooterColumn>
          <FooterColumn>
            <h4>Quicklinks</h4>
            <a href="#">Über uns</a>
            <a href="#">Team</a>
            <a href="#">Kontakt</a>
            <a href="#">Anfahrt</a>
          </FooterColumn>
          <FooterColumn>
            <h4>Rechtliches</h4>
            <a href="#">Impressum</a>
            <a href="#">Datenschutz</a>
            <a href="#">AGB</a>
          </FooterColumn>
        </FooterContent>
        <FooterBottom>
          <p>
            © 2025 Praxis Mustermann Physiotherapie. Alle Rechte vorbehalten.
          </p>
          <p>Entwickelt mit ❤️ in München</p>
        </FooterBottom>
      </Footer>
    </PageWrapper>
  );
};
